import { BACKEND_URL } from '../config/api';
import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle, XCircle, Smartphone, Share, MoreVertical, ChevronRight, Shield, Zap, MapPin } from 'lucide-react';

/**
 * OnboardingModals Component
 * 
 * Two-step onboarding for first-time users, branded to HopSeat:
 * STEP 1: Push Notifications (Mandatory)
 * STEP 2: PWA Installation Guide (Optional)
 */
const OnboardingModals = ({ user, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState('checking');
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  useEffect(() => {
    if (!user) return;

    const onboardingComplete = localStorage.getItem(`hopseat_onboarding_${user.id}`);
    
    if (onboardingComplete === 'complete') {
      setHasCompletedOnboarding(true);
      setCurrentStep(null);
      return;
    }

    if ('Notification' in window) {
      const permission = Notification.permission;
      if (permission === 'granted') {
        setNotificationStatus('granted');
        setCurrentStep('pwa');
      } else {
        setNotificationStatus('prompt');
        setCurrentStep('notifications');
      }
    } else {
      setCurrentStep('pwa');
    }
  }, [user]);

  // Wait until OneSignal SDK is available on window (loaded via index.html CDN)
  const waitForOneSignal = (timeoutMs = 8000) =>
    new Promise((resolve) => {
      if (window.OneSignal?.User) return resolve(window.OneSignal);
      const start = Date.now();
      const probe = () => {
        if (window.OneSignal?.User) return resolve(window.OneSignal);
        if (Date.now() - start > timeoutMs) return resolve(null);
        setTimeout(probe, 300);
      };
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(() => resolve(window.OneSignal));
      probe();
    });

  const waitForSubscriptionId = (maxWaitMs = 15000) =>
    new Promise((resolve) => {
      const start = Date.now();
      const probe = () => {
        const id = window.OneSignal?.User?.PushSubscription?.id;
        if (id) return resolve(id);
        if (Date.now() - start > maxWaitMs) return resolve(null);
        setTimeout(probe, 400);
      };
      probe();
    });

  const persistPlayerId = async (playerId) => {
    if (!playerId || !user?.id) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/social/onesignal-player-id`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ player_id: playerId }),
      });
    } catch (err) {
      console.warn('[Onboarding] Could not save player_id:', err?.message);
    }
  };

  // Subscribe via OneSignal v16 ONLY. Calling native Notification.requestPermission()
  // here breaks the OneSignal subscription on iOS PWAs (browser permission flips
  // to "granted" but the OneSignal Web Push subscription never registers).
  const requestNotificationPermission = async () => {
    setIsRequestingPermission(true);
    const timeout = setTimeout(() => setIsRequestingPermission(false), 18000);

    try {
      const OS = await waitForOneSignal();
      if (!OS) {
        clearTimeout(timeout);
        setIsRequestingPermission(false);
        setNotificationStatus('prompt');
        return;
      }

      // Listen for subscription change so we save the player_id the moment
      // OneSignal materialises it (handles slow iOS PWA registration).
      try {
        OS.User?.PushSubscription?.addEventListener?.('change', (e) => {
          const id = e?.current?.id || OS.User?.PushSubscription?.id;
          if (id) persistPlayerId(id);
        });
      } catch (_) { /* listener already attached or unsupported */ }

      // optIn() handles BOTH browser permission prompt AND OneSignal subscription.
      // This is the only correct entry point per OneSignal v16 docs.
      const sub = OS.User?.PushSubscription;
      try {
        if (sub?.optIn) {
          await sub.optIn();
        } else if (OS.Notifications?.requestPermission) {
          await OS.Notifications.requestPermission();
        }
      } catch (e) {
        console.warn('[Onboarding] optIn threw:', e?.message);
      }

      const playerId = await waitForSubscriptionId(15000);
      const browserPerm = window.Notification?.permission;

      clearTimeout(timeout);
      setIsRequestingPermission(false);

      if (playerId) {
        await persistPlayerId(playerId);
        setNotificationStatus('granted');
        setTimeout(() => setCurrentStep('pwa'), 1000);
      } else if (browserPerm === 'denied') {
        setNotificationStatus('denied');
      } else if (browserPerm === 'granted') {
        // iOS PWA quirk — permission shows granted, OneSignal subscription pending.
        // Treat as success; the change listener above will save the id when ready.
        setNotificationStatus('granted');
        setTimeout(() => setCurrentStep('pwa'), 1500);
      } else {
        setNotificationStatus('prompt');
      }
    } catch (error) {
      clearTimeout(timeout);
      setIsRequestingPermission(false);
      setNotificationStatus('prompt');
    }
  };

  const completeOnboarding = () => {
    if (user) {
      localStorage.setItem(`hopseat_onboarding_${user.id}`, 'complete');
      setHasCompletedOnboarding(true);
      setCurrentStep(null);
      if (onComplete) onComplete();
    }
  };

  if (!user || hasCompletedOnboarding) return null;

  // ── STEP 1: Push Notifications ──────────────────────────────────
  if (currentStep === 'notifications') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
           style={{ background: 'radial-gradient(ellipse at center, rgba(15,23,40,0.97) 0%, rgba(10,14,26,0.99) 100%)' }}
           data-testid="onboarding-notifications-modal">
        {/* Decorative glows */}
        <div className="pointer-events-none absolute -top-20 -left-20 h-72 w-72 rounded-full bg-orange-500/10 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-20 -right-20 h-60 w-60 rounded-full bg-cyan-500/8 blur-[80px]" />

        <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0f1728]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-orange-400 to-cyan-500" />

          <div className="p-8">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="h-6 w-6 text-orange-500" />
              <span className="text-xl font-bold text-orange-500">HopSeat</span>
            </div>

            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="h-2 w-8 rounded-full bg-orange-500" />
              <div className="h-2 w-8 rounded-full bg-white/10" />
            </div>

            {notificationStatus === 'prompt' && (
              <>
                {/* Icon */}
                <div className="flex justify-center mb-5">
                  <div className="rounded-2xl bg-orange-500/10 border border-orange-500/20 p-4">
                    <Bell className="h-10 w-10 text-orange-400" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-white text-center mb-2" data-testid="onboarding-notif-title">
                  Enable Gig Alerts
                </h2>
                <p className="text-sm text-slate-400 text-center mb-6">
                  Stay connected to opportunities, messages, and critical updates.
                </p>

                {/* Benefits */}
                <div className="space-y-2.5 mb-6">
                  {[
                    { icon: Zap, text: 'Real-time gig opportunities' },
                    { icon: Shield, text: 'Account & compliance alerts' },
                    { icon: Bell, text: 'Direct messages from companies' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                      <item.icon className="h-4 w-4 text-orange-400 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* Action */}
                <button
                  onClick={requestNotificationPermission}
                  disabled={isRequestingPermission}
                  className="w-full py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-all disabled:opacity-50"
                  data-testid="onboarding-enable-notif-btn"
                >
                  {isRequestingPermission ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                      Requesting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Enable Notifications
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  )}
                </button>

                <button
                  onClick={isRequestingPermission ? () => { setIsRequestingPermission(false); setNotificationStatus('prompt'); } : completeOnboarding}
                  className="w-full mt-2.5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white text-sm font-medium transition-all border border-white/5"
                  data-testid="onboarding-skip-notif-btn"
                >
                  {isRequestingPermission ? 'Cancel' : 'Skip for Now'}
                </button>
              </>
            )}

            {notificationStatus === 'granted' && (
              <div className="text-center py-6">
                <div className="flex justify-center mb-4">
                  <div className="rounded-2xl bg-green-500/10 border border-green-500/20 p-4">
                    <CheckCircle className="h-10 w-10 text-green-400" />
                  </div>
                </div>
                <p className="text-lg font-semibold text-green-400">Notifications Enabled</p>
                <p className="text-sm text-slate-400 mt-1">Setting up your experience...</p>
              </div>
            )}

            {notificationStatus === 'denied' && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4">
                    <XCircle className="h-10 w-10 text-red-400" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-white text-center mb-2">
                  Notifications Blocked
                </h2>
                <p className="text-sm text-slate-400 text-center mb-5">
                  Enable them in your browser to get gig alerts:
                </p>

                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-5">
                  <ol className="space-y-2.5 text-sm text-slate-300">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-xs font-bold">1</span>
                      Click the padlock icon in your address bar
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-xs font-bold">2</span>
                      Find "Notifications" and set to "Allow"
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-xs font-bold">3</span>
                      Refresh this page
                    </li>
                  </ol>
                </div>

                <button
                  onClick={() => window.location.reload()}
                  className="w-full py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-all"
                  data-testid="onboarding-refresh-btn"
                >
                  I've Enabled Them - Refresh
                </button>
                <button
                  onClick={completeOnboarding}
                  className="w-full mt-2.5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white text-sm font-medium transition-all border border-white/5"
                >
                  Continue Without Notifications
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 2: PWA Installation ────────────────────────────────────
  if (currentStep === 'pwa') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
           style={{ background: 'radial-gradient(ellipse at center, rgba(15,23,40,0.97) 0%, rgba(10,14,26,0.99) 100%)' }}
           data-testid="onboarding-pwa-modal">
        <div className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full bg-cyan-500/8 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-orange-500/10 blur-[80px]" />

        <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0f1728]/95 backdrop-blur-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
          <div className="h-1 w-full bg-gradient-to-r from-cyan-500 via-orange-400 to-orange-500" />

          <div className="p-8">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="h-6 w-6 text-orange-500" />
              <span className="text-xl font-bold text-orange-500">HopSeat</span>
            </div>

            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="h-2 w-8 rounded-full bg-orange-500" />
              <div className="h-2 w-8 rounded-full bg-orange-500" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-5">
              <div className="rounded-2xl bg-cyan-500/10 border border-cyan-500/20 p-4">
                <Smartphone className="h-10 w-10 text-cyan-400" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white text-center mb-2" data-testid="onboarding-pwa-title">
              Add HopSeat to Home Screen
            </h2>
            <p className="text-sm text-slate-400 text-center mb-6">
              Get the full app experience — faster loads, offline access, and one-tap launch.
            </p>

            {/* Instructions */}
            <div className="space-y-4 mb-6">
              {/* iPhone */}
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-1.5">
                    <Share className="h-4 w-4 text-blue-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-white">iPhone (Safari)</h3>
                </div>
                <ol className="space-y-2 ml-1">
                  {[
                    <>Tap <strong className="text-white">Share</strong> <Share className="h-3 w-3 inline text-blue-400" /> at the bottom</>,
                    <>Scroll and tap <strong className="text-white">"Add to Home Screen"</strong></>,
                    <>Tap <strong className="text-white">"Add"</strong> to confirm</>,
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold mt-0.5">{i+1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Android */}
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-1.5">
                    <MoreVertical className="h-4 w-4 text-green-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-white">Android (Chrome)</h3>
                </div>
                <ol className="space-y-2 ml-1">
                  {[
                    <>Tap <strong className="text-white">Menu</strong> <MoreVertical className="h-3 w-3 inline text-green-400" /> at the top right</>,
                    <>Tap <strong className="text-white">"Install App"</strong></>,
                    <>Tap <strong className="text-white">"Install"</strong> to confirm</>,
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold mt-0.5">{i+1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Tip */}
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 px-4 py-3 mb-6">
              <p className="text-xs text-cyan-300 text-center">
                <Zap className="h-3.5 w-3.5 inline mr-1" />
                Installing the app makes HopSeat load faster and work offline
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={completeOnboarding}
                className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white text-sm font-medium transition-all border border-white/5"
                data-testid="onboarding-pwa-skip-btn"
              >
                Not Now
              </button>
              <button
                onClick={completeOnboarding}
                className="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-all"
                data-testid="onboarding-pwa-done-btn"
              >
                Got it, Let's Go
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OnboardingModals;
